import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    Injector,
    Input,
    QueryList,
    ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { buildProviders, Coerce, KalAutocompleteOption, KalOptionComponent } from '@kalidea/kaligraphi';
import { delay, startWith, tap } from 'rxjs/operators';
import { CustomFormElement } from 'src/app/_core/tools/custom-form-element';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { arrayEmpty, arrayOnlyOne } from 'src/app/utils/array.utils';
import { SharedSearchbarParametersDirective } from 'src/app/_shared/directives/shared-searchbar-parameters.directive';

type SharedSearchbarList<T = string> = KalAutocompleteOption<T> & { selected?, searchbarTemplate? };

@Component({
    selector: 'app-shared-searchbar-list',
    templateUrl: './shared-searchbar-list.component.html',
    styleUrls: ['./shared-searchbar-list.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedSearchbarListComponent),
    standalone: false
})
export class SharedSearchbarListComponent extends CustomFormElement<string> implements AfterContentInit {

    formControl = new FormControl<string>(null);

    datasource: SharedSearchbarList[] = [];

    @Input()
    placeholder = 'Sélectionner une valeur';

    @Coerce('boolean')
    @Input()
    disableFirstOptionSelection = false;

    @ContentChildren(KalOptionComponent) optionList: QueryList<KalOptionComponent>;

    @ContentChildren(SharedSearchbarParametersDirective) searchbarParametersDirectives: QueryList<SharedSearchbarParametersDirective>;

    private subscription: Subscription;

    constructor(private readonly injector: Injector) {
        super();
    }

    private _selectedOption = new BehaviorSubject(null);

    get selectedOption(): string {
        return this._selectedOption.getValue();
    }

    set selectedOption(selectedOption: string) {
        this._selectedOption.next(selectedOption);
    }

    get selectedOption$(): Observable<string> {
        return this._selectedOption.asObservable();
    }

    writeValue(value: string): void {
        this.selectedOption = value;
        super.writeValue(value);
    }

    pickOption(option: SharedSearchbarList): void {
        this.formControl.patchValue(option?.label || null, {emitEvent: false});
        this.formControl.markAsPristine();
        this.notifyUpdate(option?.value || null);
        this.valueChanges.emit(option?.value || null);
    }

    clearOption(): void {
        if (this.formControl.dirty) {
            this.formControl.patchValue(null, {emitEvent: false});
            this.notifyUpdate(null);
        }
    }

    setOptionValue(): void {
        if (arrayEmpty(this.searchbarParametersDirectives.toArray())) {
            this.datasource = this.optionList.toArray().map(e => {
                return {
                    value: e.value,
                    label: e.displayLabel,
                    disabled: e.disabled
                };
            });
        } else {
            this.datasource = this.searchbarParametersDirectives.toArray().map(e => {
                const selectedOption = e.kalOptionComponent;

                return {
                    value: selectedOption.value,
                    selected: e.appSharedSearchbarParameters,
                    label: selectedOption.displayLabel,
                    searchbarTemplate: e.searchbarTemplate,
                    disabled: selectedOption.disabled
                };
            });
        }
    }

    setDisabledState(isDisabled: boolean): void {
        this.formControl[!isDisabled ? 'enable' : 'disable']({emitEvent: false});
        super.setDisabledState(isDisabled);
    }

    ngAfterContentInit(): void {
        this.control = this.createControlAndSubscriptions(this.injector);

        if (this.optionList) {
            this.watch$ = this.optionList.changes.pipe(
                delay(1),
                startWith(null),
                tap(() => {
                    this.setOptionValue();
                    this.subscription?.unsubscribe();

                    this.subscription = this.selectedOption$.pipe(
                        tap(option => {
                            if (!this.disableFirstOptionSelection && arrayOnlyOne(this.datasource) && !option) {
                                this.formControl.patchValue(this.datasource[0].label, {emitEvent: false});
                                this.notifyUpdate(this.datasource[0].value);
                            } else {
                                this.formControl.patchValue(this.datasource.find(e => e.value === option)?.label || null, {emitEvent: false});
                            }

                            this.formControl.markAsPristine();
                        })
                    ).subscribe();
                })
            );
        }
    }

}
